<script setup lang="ts">
import RelatedGames from '../../tournament/RelatedGames.vue';
import MissionReward from '../MissionReward.vue';
import { useMissionCard } from '~/composables/useMissionCard';

const props = defineProps<{
  mission: TMissionOrBadge;
  isFeatured?: boolean;
}>();

const emit = defineEmits<{
  missionOptedIn: [missionId: number];
}>();

const {
  mission,
  isFeatured,
  maxRelatedGames,
  missionProgress,
  missionProgressText,
  isModalVisible,
  isRelatedGamesModalVisible,
  isRewardModalVisible,
  formattedHintText,
  rewardData,
  buttonState,
  buttonIcons,
  handleButtonClick,
  handleMissionClick,
} = useMissionCard(props, (missionId) => emit('missionOptedIn', missionId));
</script>

<template>
  <div :class="$style.cardWrapper">
    <div
      :class="[$style.missionCard, isFeatured ? $style.featuredMission : '']"
      draggable="false"
      @dragstart.prevent
    >
      <!-- Ribbon no topo centralizado -->
      <div
        v-if="mission.ribbon"
        :class="$style.ribbon"
      >
        <span :class="$style.ribbonText">{{ mission.ribbon }}</span>
      </div>

      <!-- Header: Imagem + Nome + Descrição -->
      <div :class="$style.header">
        <SmarticoApostatudoSmarticoImage
          :alt="mission.name"
          :class="$style.image"
          :src="mission.image"
        />
        <div
          :class="$style.info"
          @click="handleMissionClick('reward')"
        >
          <p :class="$style.name">
            {{ mission.name }}
          </p>
          <p :class="$style.description">
            {{ mission.description }}
          </p>
        </div>
      </div>

      <!-- Reward Section - Verde -->
      <div
        v-if="mission.reward"
        :class="$style.rewardSection"
        @click="handleMissionClick('reward')"
      >
        <div :class="$style.rewardContent">
          <NuxtIcon
            :class="$style.giftIcon"
            :filled="true"
            name="mission/gift"
          />
          <span :class="$style.rewardText">{{ rewardData.reward }}</span>
        </div>
      </div>

      <!-- Progress Bar Section -->
      <div :class="$style.progressSection">
        <div :class="$style.progressBarContainer">
          <NuxtIcon
            :class="$style.energyIcon"
            :filled="true"
            name="mission/energy"
          />
          <div :class="$style.progressBar">
            <div
              :class="$style.progress"
              :style="{ width: `${missionProgress}%` }"
            ></div>
            <span :class="$style.progressText">{{ missionProgressText }}</span>
          </div>
          <NuxtIcon
            :class="$style.trophyIcon"
            :filled="true"
            name="mission/trophy"
          />
        </div>
      </div>

      <!-- Footer: Jogos + Botão Resgatar -->
      <div :class="$style.footer">
        <!-- Games Section -->
        <div
          :class="$style.gamesSection"
          @click="handleMissionClick('relatedGames')"
        >
          <div :class="$style.gamesWrapper">
            <div :class="$style.gamesStack">
              <div
                v-for="(related, index) in mission.related_games?.slice(
                  0,
                  maxRelatedGames,
                )"
                :key="index"
                :class="$style.gameThumb"
              >
                <NuxtImg
                  :class="$style.gameImage"
                  :src="related?.game_public_meta?.image"
                />
              </div>
            </div>
            <span :class="$style.gamesLabel">Jogos</span>
          </div>
        </div>

        <!-- Action Button -->
        <button
          v-if="buttonState"
          :class="[$style.actionButton, buttonState.class]"
          :disabled="buttonState.disabled"
          @click.stop="handleButtonClick"
        >
          <NuxtIcon
            v-for="(icon, index) in buttonIcons"
            :key="index"
            :class="buttonState.textClass"
            :name="icon"
          />
          <span :class="[$style.actionText, buttonState.textClass]">{{
            buttonState.text
          }}</span>
        </button>
      </div>

      <!-- Modals -->
      <BaseModalMissionModal v-model="isModalVisible">
        <template #header>
          <NuxtIcon
            class="text-xl text-white"
            name="mission/info"
          />
          <p class="text-lg font-bold text-white">Termos e condições</p>
        </template>
        <div v-if="mission.hint_text">
          <p
            v-for="(line, index) in formattedHintText"
            :key="index"
            class="mb-2 text-white/50 font-normal text-[13px]"
          >
            {{ line }}
          </p>
        </div>
      </BaseModalMissionModal>

      <RelatedGames
        v-model:modal-visible="isRelatedGamesModalVisible"
        :is-loading="false"
        :items="mission.related_games"
        style="display: none"
        title="Jogos Elegíveis"
      />

      <MissionReward
        v-model:modal-visible="isRewardModalVisible"
        :mission="mission"
      />
    </div>
  </div>
</template>

<style module lang="postcss">
/* === CARD WRAPPER === */
.cardWrapper {
  width: 100%;
  min-width: 0;
  padding-top: 10px;
  /* Isola contexto de composição para evitar vazamento de blur/shadow */
  isolation: isolate;
  contain: layout style paint;
}

/* Dentro de swipers: largura fixa */
:global(.afiliado-swiper__items) .cardWrapper {
  width: 165px;
  flex-shrink: 0;
}

@media (min-width: 640px) {
  .cardWrapper {
    padding-top: 14px;
  }

  :global(.afiliado-swiper__items) .cardWrapper {
    width: 260px;
  }
}

.missionCard {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding: 9px;
  gap: 4px;
  border-radius: 6px;
  background: linear-gradient(
    180deg,
    rgba(0, 43, 11, 0.2) 0%,
    rgba(0, 255, 64, 0.2) 100%
  );
  backdrop-filter: blur(6px);
  overflow: visible;
  user-select: none;
  min-width: 0;
}

.missionCard::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: inherit;
  padding: 0.57px;
  background: linear-gradient(180deg, #1d2329 0%, #00ff40 100%);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
}

@media (min-width: 640px) {
  .missionCard {
    padding: 14px;
    gap: 7px;
    border-radius: 9px;
    backdrop-filter: blur(9px);
  }

  .missionCard::before {
    padding: 0.88px;
  }
}

.featuredMission {
  /* Featured styles if needed */
}

.header {
  display: flex;
  align-items: stretch;
  gap: 6px;
  width: 100%;
}

@media (min-width: 640px) {
  .header {
    gap: 10px;
  }
}

.image {
  width: 63px;
  height: 63px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}

@media (min-width: 640px) {
  .image {
    width: 94px;
    height: 94px;
    border-radius: 6px;
  }
}

.info {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  gap: 3px;
  flex: 1;
  min-width: 0;
  height: 100%;
}

@media (min-width: 640px) {
  .info {
    gap: 5px;
  }
}

.name {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 12px;
  line-height: 15px;
  color: #ffffff;
  cursor: pointer;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  word-break: break-word;
}

.name:hover {
  filter: brightness(0.75);
}

@media (min-width: 640px) {
  .name {
    font-size: 19px;
    line-height: 22px;
  }
}

.description {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  font-size: 10px;
  line-height: 12px;
  color: #a1a1aa;
  cursor: pointer;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  word-break: break-word;
}

.description:hover {
  filter: brightness(0.75);
}

@media (min-width: 640px) {
  .description {
    font-size: 15px;
    line-height: 19px;
  }
}

.rewardSection {
  width: 100%;
  background: rgba(0, 255, 64, 0.1);
  backdrop-filter: blur(6px);
  border-radius: 6px;
  padding: 1px 10px;
  cursor: pointer;
}

.rewardSection:hover {
  filter: brightness(1.1);
}

@media (min-width: 640px) {
  .rewardSection {
    backdrop-filter: blur(10px);
    border-radius: 10px;
    padding: 2px 16px;
  }
}

.rewardContent {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 24px;
}

@media (min-width: 640px) {
  .rewardContent {
    gap: 10px;
    height: 38px;
  }
}

.giftIcon {
  font-size: 12px;
  color: #81ffa1;
}

@media (min-width: 640px) {
  .giftIcon {
    font-size: 19px;
  }
}

.rewardText {
  font-weight: 700;
  font-size: 10px;
  line-height: 10px;
  color: #81ffa1;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

@media (min-width: 640px) {
  .rewardText {
    font-size: 14px;
    line-height: 16px;
  }
}

.progressSection {
  width: 100%;
}

.progressBarContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 0;
  height: 16px;
}

@media (min-width: 640px) {
  .progressBarContainer {
    padding: 0;
    height: 25px;
  }
}

.energyIcon {
  font-size: 16px;
  margin-right: -8px;
  z-index: 10;
}

@media (min-width: 640px) {
  .energyIcon {
    font-size: 25px;
    margin-right: -12px;
  }
}

.trophyIcon {
  font-size: 18px;
  margin-left: -8px;
  z-index: 10;
}

@media (min-width: 640px) {
  .trophyIcon {
    font-size: 28px;
    margin-left: -12px;
  }
}

.progressBar {
  flex: 1;
  height: 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 64px;
  overflow: hidden;
  position: relative;
}

@media (min-width: 640px) {
  .progressBar {
    height: 19px;
    border-radius: 100px;
  }
}

.progress {
  height: 100%;
  background: #81ffa1;
  border-radius: 64px;
  min-width: fit-content;
}

@media (min-width: 640px) {
  .progress {
    border-radius: 99px;
  }
}

.progressText {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 7px;
  line-height: 6px;
  color: #010612;
}

@media (min-width: 640px) {
  .progressText {
    font-size: 11px;
    line-height: 10px;
  }
}

.footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 5px;
  width: 100%;
  min-height: 26px;
}

@media (min-width: 640px) {
  .footer {
    gap: 11px;
    min-height: 40px;
  }
}

.gamesSection {
  display: flex;
  align-items: center;
  justify-content: space-around;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 716px;
  padding: 3px 5px 3px 3px;
  cursor: pointer;
  flex: 1;
  min-width: 50%;
}

.gamesSection:hover {
  filter: brightness(1.1);
}

@media (min-width: 640px) {
  .gamesSection {
    border-radius: 1106px;
    padding: 6px 9px 6px 6px;
  }
}

.gamesWrapper {
  display: flex;
  align-items: center;
  gap: 3px;
}

@media (min-width: 640px) {
  .gamesWrapper {
    gap: 6px;
  }
}

.gamesStack {
  display: flex;
  align-items: center;
}

.gameThumb {
  width: 19px;
  height: 19px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  margin-left: -10px;
}

.gameThumb:first-child {
  margin-left: 0;
}

@media (min-width: 640px) {
  .gameThumb {
    width: 30px;
    height: 30px;
    margin-left: -22px;
  }

  .gameThumb:first-child {
    margin-left: 0;
  }
}

.gameImage {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gamesLabel {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  font-size: 9px;
  line-height: 11px;
  color: rgba(255, 255, 255, 0.5);
}

@media (min-width: 640px) {
  .gamesLabel {
    font-size: 13px;
    line-height: 18px;
  }
}

.actionButton {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 26px;
  padding: 0 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: filter 0.2s;
  flex-shrink: 1;
  min-width: 0;
  overflow: hidden;
}

.actionButton:hover {
  filter: brightness(1.2);
}

.actionButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (min-width: 640px) {
  .actionButton {
    gap: 6px;
    height: 40px;
    padding: 0 18px;
    border-radius: 9px;
  }
}

.actionText {
  font-weight: 700;
  font-size: 8px;
  line-height: 14px;
  white-space: nowrap;
}

@media (min-width: 640px) {
  .actionText {
    font-size: 13px;
    line-height: 22px;
  }
}

/* Ribbon no topo centralizado */
.ribbon {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translate(-50%, -50%);
  z-index: 10;
  min-width: 80px;
  width: auto;
  max-width: calc(100% - 46px);
  height: 18px;
  padding: 0 8px;
  background: rgba(0, 255, 64, 0.5);
  backdrop-filter: blur(8px);
  border-radius: 4px;
  border: 1px solid rgba(0, 255, 64, 0.3);
  display: inline-flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}

@media (min-width: 640px) {
  .ribbon {
    height: 22px;
    min-width: 96px;
    padding: 0 10px;
  }
}

.ribbonText {
  font-size: 9px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1;
}

@media (min-width: 640px) {
  .ribbonText {
    font-size: 11px;
  }
}
</style>
